import { Ref } from "vue"
import { Paginador } from "../UsarLeitorDeDados/usarLeitorDeDados"

interface DadoComId {
  id: string;
}

export function usarBuscarMaisDados() {
  const mapearDadosDoRepositorio = <T>(
    dadosDoRepositorio: T[],
    opcoes: {
      chavesDosDados?: string[],
      chaveDoFiltro?: string;
      concatenarCodigoNome?: boolean;
    } = {}
  ): Partial<T>[] => {
    const opcoesFinais = {
      chavesDosDados: ['id', 'nome'],
      chaveDoFiltro: 'nome',
      concatenarCodigoNome: false,
      ...opcoes,
    }

    const dadosMapeados = dadosDoRepositorio.map((dadoDoRepositorio) => {
      const dadoMapeado: Partial<T> = {}

      opcoesFinais.chavesDosDados.forEach((chaveDoDado) => {
        if (opcoes.concatenarCodigoNome && chaveDoDado === 'nome') {
          dadoMapeado[chaveDoDado] = `${dadoDoRepositorio['codigo']} - ${dadoDoRepositorio[chaveDoDado]}`
        } else {
          dadoMapeado[chaveDoDado] = dadoDoRepositorio[chaveDoDado]
        }
      })

      return dadoMapeado
    })

    return dadosMapeados
  }


  /**
   * Atualizar os dados do Dropdown com os dados adicionais obtidos através do paginador.
   *
   * @param {Paginador<T, P, C>} repositorio - Os parâmetros do paginador que busca os dados.
   * T - Tipo dos resultados retornados pelo paginador.
   * P - Tipo dos parâmetros usados para consulta no paginador.
   * C - Tipo dos cabeçalhos usados nas requisições do paginador.
   */
  const atualizarDadosDoRepositorio = async <T, P, C>(
    repositorio: Paginador<T, P, C>,
    dadosAtuais: Ref<Partial<T>[]>,
    opcoes: {
      chavesDosDados?: string[];
      chaveDoFiltro?: string;
      concatenarCodigoNome?: boolean;
    } = {}
  ): Promise<void> => {
    const opcoesFinais = {
      chavesDosDados: ['id', 'nome'],
      chaveDoFiltro: 'nome',
      concatenarCodigoNome: false,
      ...opcoes,
    }

    if (repositorio.resultado.status === "sucesso") {
      const dadosDoRepositorioMapeados = mapearDadosDoRepositorio(
        repositorio.resultado.dados,
        opcoesFinais
      )

      dadosAtuais.value = [...dadosDoRepositorioMapeados]
    }
  }

  /**
   * Busca mais dados ao fim da rolagem do Dropdown
   *
   * @param {Paginador<T, P, C>} repositorio - Os parâmetros do paginador que busca os dados.
   * T - Tipo dos resultados retornados pelo paginador.
   * P - Tipo dos parâmetros usados para consulta no paginador.
   * C - Tipo dos cabeçalhos usados nas requisições do paginador.
   */
  const buscarMaisDados = async <T, P, C>(
    repositorio: Paginador<T, P, C>,
    dadosAtuais: Ref<Partial<T>[]>,
    opcoes: {
      chavesDosDados?: string[];
      chaveDoFiltro?: string;
      concatenarCodigoNome?: boolean;
    } = {}
  ): Promise<void> => {
    const opcoesFinais = {
      chavesDosDados: ['id', 'nome'],
      chaveDoFiltro: 'nome',
      concatenarCodigoNome: false,
      ...opcoes,
    }

    if (!repositorio.possuiMaisItensNaProximaPagina()) {
      return
    }

    await repositorio.proximoComAcumulo()
    await atualizarDadosDoRepositorio(repositorio, dadosAtuais, opcoesFinais)
  }

  /**
   * Pesquisa na API o texto digitado no campo de busca do Dropdown
   *
   * @param {Paginador<T, P, C>} repositorio - Os parâmetros do paginador que busca os dados.
   * T - Tipo dos resultados retornados pelo paginador.
   * P - Tipo dos parâmetros usados para consulta no paginador.
   * C - Tipo dos cabeçalhos usados nas requisições do paginador.
   */
  const filtrarDadosDoRepositorio = async <T, P, C>(
    valorInserido: string,
    repositorio: Paginador<T, P, C>,
    dadosAtuais: Ref<Partial<T>[]>,
    opcoes: {
      chavesDosDados?: string[],
      chaveDoFiltro?: string;
      concatenarCodigoNome?: boolean;
    } = {}
  ): Promise<void> => {
    const opcoesFinais = {
      chavesDosDados: ['id', 'nome'],
      chaveDoFiltro: 'nome',
      concatenarCodigoNome: false,
      ...opcoes,
    }

    const inseriuAlgo = valorInserido.length > 0

    if (inseriuAlgo) {
      repositorio.incrementarParametros({
        [opcoesFinais.chaveDoFiltro]: `contem.${valorInserido}`,
      })
    } else {
      repositorio.resetarParametrosParaEstadoInicial()
    }
    await repositorio.pagina(0).executar()
    await atualizarDadosDoRepositorio(repositorio, dadosAtuais, opcoesFinais)
  }

  const reinserirDadoPreSelecionadoNaLista = <T extends DadoComId>(
    dadosAtuais: Ref<T[]>,
    opcaoDeDadoPreSelecionado: Ref<T> | T
  ): void => {
    let opcaoAdicionar: T
    const ehRef = (variavel: any): boolean => {
      return variavel && typeof variavel === 'object' && 'value' in variavel
    }

    if (ehRef(opcaoDeDadoPreSelecionado)) {
      opcaoAdicionar = (opcaoDeDadoPreSelecionado as Ref<T>).value
    } else {
      opcaoAdicionar = opcaoDeDadoPreSelecionado as T
    }

    const existeOpcaoCarregaNaListagem = dadosAtuais.value.find(
      (dado) => dado.id === opcaoAdicionar.id
    )

    if (!existeOpcaoCarregaNaListagem) {
      dadosAtuais.value.unshift(opcaoAdicionar)
    }

  }
  return {
    buscarMaisDados,
    atualizarDadosDoRepositorio,
    filtrarDadosDoRepositorio,
    reinserirDadoPreSelecionadoNaLista,
  }
}
